import React, { useEffect, useState, Component } from 'react';
import { Calculator as CalcIcon, Percent, CreditCard, ArrowRight } from 'lucide-react';
type Frequency = 'semanal' | 'quincenal' | 'mensual';
const FREQUENCY_CONFIG = {
  semanal: {
    label: 'Semanal',
    maxTerm: 15,
    baseInterest: 85,
    termLabel: 'semanas'
  },
  quincenal: {
    label: 'Quincenal',
    maxTerm: 10,
    baseInterest: 105,
    termLabel: 'quincenas'
  },
  mensual: {
    label: 'Mensual',
    maxTerm: 12,
    baseInterest: 170,
    termLabel: 'meses'
  }
};
// Tasa de interés escalonada según el plazo elegido
function getBaseInterest(frequency: Frequency, term: number): number {
  if (frequency === 'semanal') {
    if (term <= 3) return 85;
    if (term <= 7) return 95;
    if (term <= 12) return 105;
    return 110;
  }
  if (frequency === 'quincenal') {
    if (term <= 4) return 105;
    if (term <= 8) return 115;
    return 125;
  }
  // mensual
  if (term <= 4) return 170;
  if (term <= 8) return 195;
  return 205;
}
export function Calculator() {
  const [amount, setAmount] = useState<number>(100000);
  const [frequency, setFrequency] = useState<Frequency>('mensual');
  const [term, setTerm] = useState<number>(12);
  const [autoDebit, setAutoDebit] = useState<boolean>(false);
  const [situation, setSituation] = useState<string>('Monotributista');
  const SITUATION_OPTIONS = ['Monotributista', 'Responsable inscripto', 'Autónomo', 'Jubilado', 'Profesional independiente', 'Comerciante', 'Estudiante', 'Otro'];
  // Reset term if frequency changes and current term is > maxTerm
  useEffect(() => {
    const max = FREQUENCY_CONFIG[frequency].maxTerm;
    if (term > max) {
      setTerm(max);
    }
  }, [frequency]);
  const config = FREQUENCY_CONFIG[frequency];
  // Calculate interest
  // Tasa base escalonada según el plazo elegido
  const baseInterest = getBaseInterest(frequency, term);
  // If auto debit, 10% discount on interest rate
  const actualInterestRate = autoDebit ? baseInterest * 0.9 : baseInterest;
  const totalInterest = amount * (actualInterestRate / 100);
  const totalToPay = amount + totalInterest;
  const installmentAmount = totalToPay / term;
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0
    }).format(value);
  };
  const handleWhatsApp = () => {
    const phone = '5491122696510';
    const debitText = autoDebit ? '✅ Adherido a débito automático (10% de descuento en intereses)' : '❌ Sin débito automático';
    const message = `¡Hola Finance Group! 👋\n\nQuiero solicitar un crédito con las siguientes características:\n\n💰 *Monto solicitado:* ${formatCurrency(amount)}\n👤 *Situación:* ${situation}\n🗓️ *Frecuencia de pago:* ${config.label}\n🔢 *Plazo:* ${term} ${term === 1 ? config.termLabel.slice(0, -1) : config.termLabel}\n💳 *Cuota estimada:* ${formatCurrency(installmentAmount)}\n${debitText}\n\n¿Me podrían asesorar con los requisitos?`;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };
  return <section id="simulador" className="py-20 bg-white" data-id="element-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-13">
        <div className="text-center mb-12" data-id="element-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-blue-900 mb-4" data-id="element-15">
            Simulador de Crédito
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-16">
            Calculá tu préstamo en segundos. Elegí el monto, la frecuencia de
            pago y descubrí tu cuota ideal.
          </p>
        </div>

        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden flex flex-col md:flex-row" data-id="element-17">
          {/* Controles */}
          <div className="flex-1 p-8 md:p-10 bg-gray-50 border-r border-gray-100" data-id="element-18">
            <div className="space-y-8" data-id="element-19">
              {/* Monto */}
              <div data-id="element-20">
                <label className="flex justify-between text-sm font-bold text-gray-700 mb-4" data-id="element-21">
                  <span data-id="element-22">Monto a solicitar</span>
                  <span className="text-blue-900 text-xl" data-id="element-23">
                    {formatCurrency(amount)}
                  </span>
                </label>
                <input type="range" min="0" max="2000000" step="10000" value={amount} onChange={e => setAmount(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-900" data-id="element-24" />
                <div className="flex justify-between text-xs text-gray-500 mt-2 font-medium" data-id="element-25">
                  <span data-id="element-26">$0</span>
                  <span data-id="element-27">$2.000.000</span>
                </div>

                {/* Ingreso manual del monto */}
                <div className="mt-4" data-id="element-28">
                  <label htmlFor="amountInput" className="block text-xs font-semibold text-gray-600 mb-1" data-id="element-29">
                    O ingresá el monto manualmente
                  </label>
                  <div className="relative" data-id="element-30">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium" data-id="element-31">
                      $
                    </span>
                    <input id="amountInput" type="number" min={0} max={2000000} step={1000} value={amount} onChange={e => {
                    const raw = Number(e.target.value);
                    const clamped = Math.min(2000000, Math.max(0, isNaN(raw) ? 0 : raw));
                    setAmount(clamped);
                  }} className="w-full bg-white border border-gray-300 text-gray-900 text-base rounded-lg focus:ring-blue-900 focus:border-blue-900 block py-3 pl-7 pr-3 font-medium" placeholder="Ej: 350000" data-id="element-32" />
                  </div>
                </div>
              </div>

              {/* Situación */}
              <div data-id="element-33">
                <label htmlFor="situation" className="block text-sm font-bold text-gray-700 mb-3" data-id="element-34">
                  Situación
                </label>
                <select id="situation" value={situation} onChange={e => setSituation(e.target.value)} className="w-full bg-white border border-gray-300 text-gray-900 text-base rounded-lg focus:ring-blue-900 focus:border-blue-900 block p-3 font-medium" data-id="element-35">
                  {SITUATION_OPTIONS.map(opt => <option key={opt} value={opt} data-id="element-36">
                      {opt}
                    </option>)}
                </select>
              </div>

              {/* Frecuencia */}
              <div data-id="element-37">
                <label className="block text-sm font-bold text-gray-700 mb-3" data-id="element-38">
                  Frecuencia de pago
                </label>
                <div className="grid grid-cols-3 gap-3" data-id="element-39">
                  {(Object.keys(FREQUENCY_CONFIG) as Frequency[]).map(freq => <button key={freq} onClick={() => setFrequency(freq)} className={`py-2 px-1 text-sm font-semibold rounded-lg border transition-all ${frequency === freq ? 'bg-blue-900 text-white border-blue-900 shadow-md' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-900 hover:text-blue-900'}`} data-id="element-40">
                        {FREQUENCY_CONFIG[freq].label}
                      </button>)}
                </div>
              </div>

              {/* Plazo */}
              <div data-id="element-41">
                <label className="block text-sm font-bold text-gray-700 mb-3" data-id="element-42">
                  Plazo ({config.termLabel})
                </label>
                <select value={term} onChange={e => setTerm(Number(e.target.value))} className="w-full bg-white border border-gray-300 text-gray-900 text-base rounded-lg focus:ring-blue-900 focus:border-blue-900 block p-3 font-medium" data-id="element-43">
                  {Array.from({
                  length: config.maxTerm
                }, (_, i) => i + 1).map(n => <option key={n} value={n} data-id="element-44">
                      {n}{' '}
                      {n === 1 ? config.termLabel.slice(0, -1) : config.termLabel}
                    </option>)}
                </select>
              </div>

              {/* Débito Automático */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3" data-id="element-45">
                <div className="flex items-center h-5 mt-0.5" data-id="element-46">
                  <input id="autoDebit" type="checkbox" checked={autoDebit} onChange={e => setAutoDebit(e.target.checked)} className="w-5 h-5 text-blue-900 bg-white border-gray-300 rounded focus:ring-blue-900 focus:ring-2 cursor-pointer" data-id="element-47" />
                </div>
                <div className="text-sm" data-id="element-48">
                  <label htmlFor="autoDebit" className="font-bold text-blue-900 cursor-pointer" data-id="element-49">
                    Adherir a Débito Automático
                  </label>
                  <p className="text-blue-800 mt-1" data-id="element-50">
                    ¡Obtené un{' '}
                    <span className="font-bold" data-id="element-51">10% de descuento</span> en los
                    intereses!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Resultados */}
          <div className="flex-1 p-8 md:p-10 bg-blue-900 text-white flex flex-col justify-center" data-id="element-52">
            <div className="mb-8" data-id="element-53">
              <h3 className="text-blue-200 text-sm font-bold uppercase tracking-wider mb-2" data-id="element-54">
                Tu cuota {frequency} será de
              </h3>
              <div className="text-5xl font-extrabold text-white mb-2" data-id="element-55">
                {formatCurrency(installmentAmount)}
              </div>
              <p className="text-blue-200 text-sm" data-id="element-56">
                Durante {term}{' '}
                {term === 1 ? config.termLabel.slice(0, -1) : config.termLabel}
              </p>
            </div>

            <div className="space-y-4 mb-10" data-id="element-57">
              <div className="flex justify-between text-sm border-b border-blue-800 pb-3" data-id="element-58">
                <span className="text-blue-200" data-id="element-59">Monto solicitado</span>
                <span className="font-bold" data-id="element-60">{formatCurrency(amount)}</span>
              </div>
              <div className="flex justify-between text-sm border-b border-blue-800 pb-3" data-id="element-61">
                <span className="text-blue-200" data-id="element-62">Tasa de interés aplicada</span>
                <span className="font-bold" data-id="element-63">
                  {actualInterestRate.toFixed(1)}%
                  {autoDebit && <span className="text-green-400 ml-1 text-xs" data-id="element-64">
                      (Desc. aplicado)
                    </span>}
                </span>
              </div>
              <div className="flex justify-between text-sm border-b border-blue-800 pb-3" data-id="element-65">
                <span className="text-blue-200" data-id="element-66">Total a devolver</span>
                <span className="font-bold" data-id="element-67">{formatCurrency(totalToPay)}</span>
              </div>
            </div>

            <button onClick={handleWhatsApp} className="w-full bg-green-500 hover:bg-green-400 text-white font-bold py-4 px-6 rounded-xl flex items-center justify-center gap-2 transition-colors shadow-lg hover:shadow-xl text-lg" data-id="element-68">
              Solicitar Ahora
              <ArrowRight className="w-5 h-5" data-id="element-69" />
            </button>
            <p className="text-center text-xs text-blue-300 mt-4" data-id="element-70">
              Sujeto a aprobación crediticia.
            </p>
          </div>
        </div>
      </div>
    </section>;
}